import React from 'react'
import { Redirect } from 'react-router'

import { localStorageKeys } from '@sketch/constants'
import { routes, DynamicLoadingPage } from '@sketch/modules-common'

// Utils
import { removeItem } from '@sketch/utils'
import { useUserAuthorizations, mapSsoStartUrl } from 'modules/sso/operations'
import useSignOut from 'modules/user/useSignOut'
import { isSsoAuthorization } from 'graphql/multisession'

// Components
import WorkspaceAgnosticRedirectRoute from './WorkspaceAgnosticRedirectRoute'

// GQL
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

interface FallbackWorkspaceRedirectRouteProps {
  workspaces: WorkspaceMinimalFragment[]
  personalWorkspace?: WorkspaceMinimalFragment
}

/**
 * This component redirects users when they try to access the default ENTRY
 * page and there is no direct way to know where should they go.
 *
 * We know we can't redirect the user to their last workspace seen, either
 * because they don't have one or because they don't have access to it. Once
 * here, we just try to find a matching workspace the user can see or we just
 * sign them out.
 */
export const FallbackWorkspaceRedirectRoute = ({
  workspaces,
  personalWorkspace,
}: FallbackWorkspaceRedirectRouteProps) => {
  const { authorizations, hasPersonalAuthorization } = useUserAuthorizations()
  const signOut = useSignOut({
    location: routes.SIGN_UP.create({}),
    reason: 'Workspace Redirect Fallback',
  })

  removeItem(localStorageKeys.lastWorkspaceIdKey)

  if (hasPersonalAuthorization) {
    /**
     * Let's see if the user has a personal workspace where we can send them to.
     */
    if (personalWorkspace) {
      return (
        <Redirect
          to={routes.WORKSPACE_SHARES.create({
            workspaceId: personalWorkspace.identifier,
          })}
        />
      )
    }

    /**
     * User has no personal workspace so let's just look for the first non-SSO
     * workspace and redirect them there.
     */
    const firstNonSsoWorkspace = workspaces.find(
      workspace => !workspace.customer?.ssoEnabled
    )

    if (firstNonSsoWorkspace) {
      if (
        firstNonSsoWorkspace.userRole === 'FINANCE' ||
        firstNonSsoWorkspace.userRole === 'PARTNER'
      ) {
        return (
          <WorkspaceAgnosticRedirectRoute
            workspaceId={firstNonSsoWorkspace.identifier}
            fallbackRoute={routes.WORKSPACE_SETTINGS_PEOPLE.create({
              workspaceId: firstNonSsoWorkspace.identifier,
            })}
          />
        )
      } else {
        return (
          <WorkspaceAgnosticRedirectRoute
            workspaceId={firstNonSsoWorkspace.identifier}
            fallbackRoute={routes.WORKSPACE_SHARES.create({
              workspaceId: firstNonSsoWorkspace.identifier,
            })}
          />
        )
      }
    }
  }

  /**
   * If the user is authenticated but only has SSO sessions OR they only have
   * access to SSO workspaces, then we look for the best matching workspace we
   * can find from their session information.
   */
  const availableWorkspaceIds = authorizations.map(auth =>
    isSsoAuthorization(auth) ? auth.workspaceId : null
  )

  const availableWorkspace = workspaces.find(workspace =>
    availableWorkspaceIds.includes(workspace.identifier)
  )

  /**
   * We found a SSO workspace the user have access to with their SSO
   * credentials.
   */
  if (availableWorkspace) {
    if (
      availableWorkspace.userRole === 'FINANCE' ||
      availableWorkspace.userRole === 'PARTNER'
    ) {
      return (
        <Redirect
          to={routes.WORKSPACE_SETTINGS_PEOPLE.create({
            workspaceId: availableWorkspace.identifier,
          })}
        />
      )
    } else {
      return (
        <Redirect
          to={routes.WORKSPACE_SHARES.create({
            workspaceId: availableWorkspace.identifier,
          })}
        />
      )
    }
  }

  const firstSsoWorkspace = workspaces.find(
    workspace => workspace.customer?.ssoEnabled
  )

  if (firstSsoWorkspace) {
    /**
     * At this point, the user only has SSO workspaces. Here we try
     * to see if the user is Admin of a SSO Workspace. This allows
     * Admins to sign in into SSO workspaces with U+P accounts
     */
    const isAdminOfFirstSsoWorkspace = firstSsoWorkspace?.userRole === 'ADMIN'

    if (isAdminOfFirstSsoWorkspace) {
      return (
        <WorkspaceAgnosticRedirectRoute
          workspaceId={firstSsoWorkspace.identifier}
          fallbackRoute={routes.WORKSPACE_SETTINGS_PEOPLE.create({
            workspaceId: firstSsoWorkspace.identifier,
          })}
        />
      )
    }

    /*
     * From here on, they don't have access to any SSO workspace
     * We will try signing them into the first SSO workspace we find.
     */
    if (firstSsoWorkspace.customer?.ssoStartUrl) {
      window.location.href = mapSsoStartUrl(
        firstSsoWorkspace.customer.ssoStartUrl
      )

      return <DynamicLoadingPage />
    }
  }

  /**
   * If everything else fails we just sign out the user.
   * This happens ONLY once we made sure there is no workspace we can redirect
   * the user to:
   *   * The user has no last workspace seen.
   *   * The user has no access to their last workspace seen.
   *   * The user has no access to any non-SSO workspace.
   *   * The user does not belong to any SSO workspace.
   */
  signOut()
  return <DynamicLoadingPage />
}
